import React from 'react';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../../styles/tokens';

export interface PriceHistoryPoint {
  timestamp: string;
  price: number;
}

interface PriceHistoryChartProps {
  data: PriceHistoryPoint[];
  height?: number;
  title?: string;
}

/**
 * Lightweight sparkline-style chart for price history until a full charting lib is introduced.
 */
export function PriceHistoryChart({ data, height = 140, title = 'Price History' }: PriceHistoryChartProps) {
  if (!data || data.length === 0) {
    return <div style={emptyStyle}>No price history yet</div>;
  }

  const prices = data.map((d) => d.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const range = Math.max(maxPrice - minPrice, 0.01);

  const points = data.map((d, idx) => {
    const x = (idx / Math.max(data.length - 1, 1)) * 100;
    const y = ((maxPrice - d.price) / range) * 100;
    return `${x},${y}`;
  });

  const path = points.join(' ');

  return (
    <div style={containerStyle(height)}>
      <div style={headerStyle}>{title}</div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={svgStyle} role="img" aria-label={title}>
        <polyline
          fill="none"
          stroke={colors.primary}
          strokeWidth={2}
          points={path}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div style={legendStyle}>
        <span>Min: ${minPrice.toFixed(2)}</span>
        <span>Max: ${maxPrice.toFixed(2)}</span>
      </div>
    </div>
  );
}

const containerStyle = (height: number): React.CSSProperties => ({
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: borderRadius.md,
  padding: spacing.md,
  height,
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.sm,
});

const headerStyle: React.CSSProperties = {
  fontSize: typography.small.fontSize,
  fontWeight: typographyFlat.fontWeight.semibold,
  color: colors.text,
};

const svgStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
};

const legendStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  color: colors.textSecondary,
  fontSize: typography.small.fontSize,
};

const emptyStyle: React.CSSProperties = {
  border: `1px dashed ${colors.border}`,
  borderRadius: borderRadius.md,
  padding: spacing.lg,
  color: colors.textSecondary,
  textAlign: 'center',
};
